import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { logoutUsers } from "../../store/UserSlice";
import { toast } from "react-toastify";
import DropdownUser from "../../components/DropdownUser";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { getLangFromPathname, getLocalizedPath } from "../../components/LanguageRouteSync";
import Menu from "./menu";

const Header = () => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const isLoggedIn = JSON.parse(localStorage.getItem('isLoggedIn') as string);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const { t } = useTranslation();
  const currentLang = getLangFromPathname(location.pathname);

  const toggleMenu = () => {
    setMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    dispatch(logoutUsers()).then(() => {
      toast.success("Logged out Successfully.");
      navigate(getLocalizedPath('/', currentLang));
    });
  };

  return (
    <nav className="bg-cover bg-no-repeat bg-bottom bg-[#0503326b] p-3 absolute w-full z-10 border-b border-[rgba(255, 255, 255,0.3)]">
      <div className="container mx-auto flex items-center justify-between">
        <div>
          <Link to={getLocalizedPath('/', currentLang)}>
            <img
              src="./images/Seva-logo.png"
              alt="Logo"
              className="h-16"
            />
          </Link>
        </div>

        {/* Container for Navigation, Language Switcher and Login Button for Desktop */}
        <div className="hidden md:flex items-center space-x-8 justify-end">
          <a
            href={getLocalizedPath('/', currentLang) + '#about'}
            className="w-auto text-white text-lg font-semibold leading-10 hover:text-[#037897] transition-colors"
          >
            {t("nav.about")}
          </a>
          <Link
            to={getLocalizedPath('/programs', currentLang)}
            className="w-auto text-white text-lg font-semibold leading-10 hover:text-[#037897] transition-colors"
          >
            {t("nav.programs")}
          </Link>
          <Link
            to={getLocalizedPath('/contact', currentLang)}
            className="w-auto text-white text-lg font-semibold leading-10 hover:text-[#037897] transition-colors border-r pr-8 border-[#E4E4E4]"
          >
            {t("nav.contact")}
          </Link>

          {/* Language Switcher (Hidden for now) */}
          {/* <div className="border-l border-r border-[#E4E4E4]/30 px-4 py-1">
            <LanguageSwitcher />
          </div> */}

          {/* Login Button for Desktop */}
          {isLoggedIn ? (
            <div className="homep">
              <DropdownUser />
            </div>
          ) : (
            <Link
              to={getLocalizedPath('/login', currentLang)}
              className="bg-[#037897] relative flex h-[46px] rounded-lg min-w-[120px] px-4 items-center justify-center overflow-hidden uppercase font-semibold text-white transition ease-in-out duration-250 focus:outline-none focus:shadow-outline hover:bg-[#025f77]"
            >
              <svg width="15" height="18" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline mr-2" >
                <circle cx="8.57897" cy="5.77803" r="4.77803" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path fillRule="evenodd" clipRule="evenodd" d="M1.00002 17.2013C0.998732 16.8654 1.07385 16.5336 1.2197 16.2311C1.67736 15.3157 2.96798 14.8306 4.03892 14.6109C4.81128 14.4461 5.59431 14.336 6.38217 14.2814C7.84084 14.1533 9.30793 14.1533 10.7666 14.2814C11.5544 14.3366 12.3374 14.4467 13.1099 14.6109C14.1808 14.8306 15.4714 15.27 15.9291 16.2311C16.2224 16.8479 16.2224 17.5639 15.9291 18.1807C15.4714 19.1418 14.1808 19.5812 13.1099 19.7917C12.3384 19.9633 11.5551 20.0766 10.7666 20.1304C9.57937 20.231 8.38659 20.2494 7.19681 20.1853C6.92221 20.1853 6.65677 20.1853 6.38217 20.1304C5.59663 20.0772 4.81632 19.964 4.04807 19.7917C2.96798 19.5812 1.68652 19.1418 1.2197 18.1807C1.0746 17.8746 0.999552 17.54 1.00002 17.2013Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>{" "}
              {t("nav.login")}
            </Link>
          )}
        </div>

        {/* Menu Button for Mobile */}
        <div className="md:hidden">
          <button
            className="text-white focus:outline-none"
            onClick={toggleMenu}
            aria-label="Toggle Navigation Menu"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </button>
        </div>

        {/* Responsive Menu */}
        <div
          className={`fixed top-0 left-0 w-full h-full bg-white z-50 transform ${
            isMenuOpen ? "translate-x-0" : "-translate-x-full"
          } transition-transform duration-300 ease-in-out md:hidden`}
        >
          <div className="flex items-center justify-end p-8">
            <button
              className="text-gray-800 focus:outline-none"
              onClick={toggleMenu}
              aria-label="Close Menu"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="text-center px-6">
            <Menu onItemClick={() => setMenuOpen(false)} />

            {/* Mobile Language Switcher (Hidden for now) */}
            {/* <div className="my-4 border-t border-b border-gray-100 py-2">
              <LanguageSwitcher isMobile={true} />
            </div> */}

            {/* Login Button for Mobile */}
            {isLoggedIn ? (
              <div className="align-middle text-center justify-center flex">
                <DropdownUser />
              </div>
            ) : (
              <Link
                to={getLocalizedPath('/login', currentLang)}
                onClick={() => setMenuOpen(false)}
                className="inline-flex items-center text-white bg-[#037897] px-8 uppercase font-semibold rounded-lg py-3"
              >
                <svg width="15" height="18" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline mr-2" >
                  <circle cx="8.57897" cy="5.77803" r="4.77803" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path fillRule="evenodd" clipRule="evenodd" d="M1.00002 17.2013C0.998732 16.8654 1.07385 16.5336 1.2197 16.2311C1.67736 15.3157 2.96798 14.8306 4.03892 14.6109C4.81128 14.4461 5.59431 14.336 6.38217 14.2814C7.84084 14.1533 9.30793 14.1533 10.7666 14.2814C11.5544 14.3366 12.3374 14.4467 13.1099 14.6109C14.1808 14.8306 15.4714 15.27 15.9291 16.2311C16.2224 16.8479 16.2224 17.5639 15.9291 18.1807C15.4714 19.1418 14.1808 19.5812 13.1099 19.7917C12.3384 19.9633 11.5551 20.0766 10.7666 20.1304C9.57937 20.231 8.38659 20.2494 7.19681 20.1853C6.92221 20.1853 6.65677 20.1853 6.38217 20.1304C5.59663 20.0772 4.81632 19.964 4.04807 19.7917C2.96798 19.5812 1.68652 19.1418 1.2197 18.1807C1.0746 17.8746 0.999552 17.54 1.00002 17.2013Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>{" "}
                {t("nav.login")}
              </Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;
